import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';
import { debounce } from 'lodash';
import { TextField } from '../NewDesign/Inputs/TextField';
import Radios from '../NewDesign/Inputs/Radios';
import Checkboxes from '../NewDesign/Inputs/Checkboxes';
import YesNoRadio from '../NewDesign/Inputs/YesNoRadio';
import {
  Card,
  CardBody,
  CardHeader,
  CardSubHeader,
} from '../NewDesign/Card/Card';
import { PageTitle, Section } from '../NewDesign/Layout/Layout';
import { ButtonClassic } from '../NewDesign/Buttons/Buttons';
import SelectInput from '../NewDesign/Inputs/SelectInput';
// eslint-disable-next-line import/no-cycle
import SubForm from '../NewDesign/Inputs/SubForm'; // Known circular dependency which is required for SubForm to work
import FileInputV2 from '../FileInputV2/FileInputV2';
import AddressInput from './AddressInput';
import countries from '../../../Lib/Constants/countries';
import states from '../../../Lib/Constants/states';

const formPageShape = PropTypes.shape({
  pageTitle: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          name: PropTypes.string,
          type: PropTypes.string,
          deleteConfirmation: PropTypes.shape({
            deleteTitle: PropTypes.string,
            deleteText: PropTypes.string,
          }),
          disabled: PropTypes.bool,
          required: PropTypes.bool,
          value: PropTypes.string,
          suffix: PropTypes.string,
          prefix: PropTypes.string,
          multiple: PropTypes.bool,
          specialValidation: PropTypes.object,
          min: PropTypes.number,
          options: PropTypes.arrayOf(
            PropTypes.shape({
              label: PropTypes.string.isRequired,
              value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            }),
          ),
        }),
      ),
    }).isRequired,
  ),
});

const getFieldDefaultValue = (field) => {
  if (field.type === 'checkbox') {
    return [];
  }
  if (field.type === 'select') {
    return field.multiple ? [] : null;
  }
  if (field.type === 'subForm') {
    return [];
  }
  if (field.type === 'file') {
    return false;
  }
  if (field.value !== undefined) {
    return field.value;
  }
  return field.default !== undefined ? field.default : '';
};

const evaluateConditions = (currentFormData, formPage) => {
  const updatedFormData = { ...currentFormData };
  let isFormDataUpdated = false;

  formPage.cards.forEach((card) => {
    if (!card.fields) {
      return;
    }
    card.fields.forEach((field) => {
      if (field.cond) {
        const conditionMet = field.cond(updatedFormData);
        if (conditionMet && !(field.name in updatedFormData)) {
          updatedFormData[field.name] = getFieldDefaultValue(field);
          isFormDataUpdated = true;
        } else if (!conditionMet && field.name in updatedFormData) {
          delete updatedFormData[field.name];
          isFormDataUpdated = true;
        }
      }
    });
  });

  return isFormDataUpdated ? updatedFormData : currentFormData;
};

const initializeFormData = (formPage, formsData) => {
  const initialState = {};

  formPage.cards.forEach((card) => {
    if (!card.fields) {
      return;
    }
    card.fields.forEach((field) => {
      if (field.type === 'address') {
        Object.values(field.names).forEach((name) => {
          if (formsData[name] !== undefined) {
            initialState[name] = formsData[name];
            return;
          }
          initialState[name] = '';
        });
        return;
      }

      if (formsData[field.name] !== undefined) {
        initialState[field.name] = formsData[field.name];
        return;
      }

      initialState[field.name] = getFieldDefaultValue(field);
    });
  });
  return initialState;
};

function FieldRenderer({
  field,
  handleChange,
  handleMultiSelect,
  formData,
  errorField,
  errorMessages,
  processedLabel,
  viewMode,
  locked,
  level,
  getUploadUrl,
  getDownloadUrl,
}) {
  if (field.cond && !field.cond(formData)) {
    return null;
  }

  const handleTextChange = (fieldKey, value) => {
    if (
      field.default !== undefined &&
      field.default !== value &&
      (value === '' || !value?.replace(/\s/g, '').length)
    ) {
      handleChange(fieldKey, field.default);
      return;
    }
    handleChange(fieldKey, value);
  };

  switch (field.type) {
    case 'address':
      return (
        <AddressInput
          field={field}
          key={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleChange}
          errorField={errorField}
          disabled={field.disabled || locked}
          viewMode={viewMode}
          errorMessages={errorMessages}
        />
      );
    case 'text':
      return (
        <TextField
          key={field.name}
          inputName={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleTextChange}
          suffix={field.suffix}
          prefix={field.prefix}
          error={errorField.includes(field.name)}
          required={field.required}
          disabled={field.disabled || locked}
          type={field.inputType}
          multiline={!!field.multiline}
          viewMode={viewMode}
          errorText={errorMessages[field.name]}
        />
      );
    case 'radios':
      return (
        <Radios
          key={field.name}
          inputName={field.name}
          name={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleChange}
          error={errorField.includes(field.name)}
          required={field.required}
          options={field.options}
          viewMode={viewMode}
          disabled={field.disabled || locked}
        />
      );
    case 'file':
      return (
        <FileInputV2
          key={field.name}
          label={processedLabel}
          name={field.name}
          required={field.required}
          disabled={field.disabled || locked}
          viewMode={viewMode}
          error={errorField.includes(field.name)}
          fileType="application/pdf"
          sizeLimit={10}
          isUploaded={!!formData[field.name]}
          onChange={handleChange}
          getUploadUrl={getUploadUrl}
          getDownloadUrl={getDownloadUrl}
        />
      );
    case 'checkbox':
      return (
        <Checkboxes
          key={field.name}
          inputName={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleMultiSelect}
          error={errorField.includes(field.name)}
          required={field.required}
          options={field.options}
          viewMode={viewMode}
          disabled={field.disabled || locked}
        />
      );
    case 'yes_no':
      return (
        <YesNoRadio
          key={field.name}
          inputName={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleChange}
          error={errorField.includes(field.name)}
          required={field.required}
          viewMode={viewMode}
          disabled={field.disabled || locked}
        />
      );
    case 'select':
      return (
        <SelectInput
          key={field.name}
          inputName={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleChange}
          error={errorField.includes(field.name)}
          required={field.required}
          multiple={field.multiple}
          options={field.options}
          viewMode={viewMode}
          disabled={field.disabled || locked}
        />
      );
    case 'text_or_select':
      return field.condSelect(formData) ? (
        <SelectInput
          key={field.name}
          inputName={field.name}
          label={field.labelSelect}
          formData={formData}
          onChange={handleChange}
          error={errorField.includes(field.name)}
          required={field.required}
          multiple={false}
          options={field.options}
          viewMode={viewMode}
          disabled={field.disabled || locked}
        />
      ) : (
        <TextField
          key={field.name}
          inputName={field.name}
          label={processedLabel}
          formData={formData}
          onChange={handleChange}
          suffix={field.suffix}
          prefix={field.prefix}
          error={errorField.includes(field.name)}
          required={field.required}
          disabled={field.disabled || locked}
          viewMode={viewMode}
          errorText={errorMessages[field.name]}
        />
      );
    case 'subForm':
      return (
        <SubForm
          buttonText={field.buttonText}
          name={field.name}
          formData={formData}
          label={processedLabel}
          subForm={field.subForm}
          errorText={errorMessages[field.name]}
          onSave={handleChange}
          idKey={field.idKey}
          viewMode={viewMode}
          disabled={field.disabled || locked}
          deleteConfirmation={field.deleteConfirmation}
          getUploadUrl={getUploadUrl}
          getDownloadUrl={getDownloadUrl}
          level={level}
        />
      );
    default:
      return <div key={field.name}>Unsupported field type</div>;
  }
}

const shouldRerenderFields = (
  prevProps,
  nextProps,
  prevFieldName,
  nextFieldName,
) => {
  if (prevProps.formData[prevFieldName] !== nextProps.formData[nextFieldName]) {
    return false;
  }

  /* Re-render if the value for a specific field in the form data has changed */
  if (prevProps.formData[prevFieldName] !== nextProps.formData[nextFieldName]) {
    return false;
  }

  /* Re-render if a specific field is added or removed to the error field */
  if (
    prevProps.errorField.includes(prevFieldName) !==
    nextProps.errorField.includes(nextFieldName)
  ) {
    return false;
  }

  /* Re-render if the error message of a specific field has changed */
  return (
    prevProps.errorMessages[prevFieldName] ===
    nextProps.errorMessages[nextFieldName]
  );
};

const FieldRendererMemoized = React.memo(
  FieldRenderer,
  (prevProps, nextProps) => {
    /* Verify if any address sub-field value has not changed and should not re-render */
    if (prevProps.field.type === 'address') {
      if (
        Object.values(prevProps.field.names).some(
          (name) => !shouldRerenderFields(prevProps, nextProps, name, name),
        )
      ) {
        return false;
      }
    }

    /* Verify if any sub-form field value has not changed and should not re-render */
    if (
      !shouldRerenderFields(
        prevProps,
        nextProps,
        prevProps.field.name,
        nextProps.field.name,
      )
    ) {
      return false;
    }

    /* Re-render if the view mode has changed */
    if (prevProps.viewMode !== nextProps.viewMode) {
      return false;
    }

    /* Re-render if the locked mode has changed */
    if (prevProps.locked !== nextProps.locked) {
      return false;
    }

    /* Re-render if the field condition to render as a select has changed */
    if (prevProps.field.condSelect && nextProps.field.condSelect) {
      if (
        prevProps.field.condSelect(prevProps.formData) !==
        nextProps.field.condSelect(nextProps.formData)
      ) {
        return false;
      }
    }

    return true; // Re-render if false is passed.
  },
);

function RenderCard({
  card,
  handleChange,
  handleMultiSelect,
  formData,
  errorField,
  errorMessages,
  processedLabels,
  viewMode,
  nested,
  locked,
  level,
  getUploadUrl,
  getDownloadUrl,
}) {
  return (
    <Card key={card.title} style={{ boxShadow: 'none' }} noShadow={nested}>
      <CardHeader>{card.title}</CardHeader>
      {card.subTitle && <CardSubHeader>{card.subTitle}</CardSubHeader>}
      {card.fields && (
        <CardBody>
          {card.fields.map((field) => (
            <FieldRendererMemoized
              key={`${field.name}wrapper`}
              field={field}
              processedLabel={processedLabels[field.name]}
              handleChange={handleChange}
              handleMultiSelect={handleMultiSelect}
              formData={formData}
              errorField={errorField}
              errorMessages={errorMessages}
              viewMode={viewMode}
              locked={locked}
              level={level}
              getUploadUrl={getUploadUrl}
              getDownloadUrl={getDownloadUrl}
            />
          ))}
        </CardBody>
      )}
    </Card>
  );
}

RenderCard.propTypes = {
  card: PropTypes.shape({}).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleMultiSelect: PropTypes.func.isRequired,
  formData: PropTypes.shape({}).isRequired,
  errorField: PropTypes.arrayOf(PropTypes.string).isRequired,
  errorMessages: PropTypes.shape({}).isRequired,
  processedLabels: PropTypes.shape({}).isRequired,
  viewMode: PropTypes.bool.isRequired,
  nested: PropTypes.bool.isRequired,
  locked: PropTypes.bool.isRequired,
  level: PropTypes.number.isRequired,
  getUploadUrl: PropTypes.func.isRequired,
  getDownloadUrl: PropTypes.func.isRequired,
};

function RenderFormPage({
  formPage,
  handleChange,
  handleMultiSelect,
  buttonText,
  handleSubmit,
  formData,
  errorField,
  errorMessages,
  processedLabels,
  viewMode,
  nested,
  locked,
  level,
  getUploadUrl,
  getDownloadUrl,
}) {
  return (
    <Section key={formPage.pageTitle}>
      {!(nested && viewMode) && <PageTitle>{formPage.pageTitle}</PageTitle>}
      {formPage.cards &&
        formPage.cards.map((card) => (
          <RenderCard
            key={card.title}
            card={card}
            handleChange={handleChange}
            handleMultiSelect={handleMultiSelect}
            formData={formData}
            errorField={errorField}
            errorMessages={errorMessages}
            processedLabels={processedLabels}
            viewMode={viewMode}
            nested={nested}
            locked={locked}
            level={level}
            getUploadUrl={getUploadUrl}
            getDownloadUrl={getDownloadUrl}
          />
        ))}
      {(viewMode === true && nested === false) || !buttonText ? null : (
        <ButtonClassic onClick={handleSubmit} disabled={locked}>
          {buttonText}
        </ButtonClassic>
      )}
    </Section>
  );
}

RenderFormPage.propTypes = {
  formPage: PropTypes.oneOfType([
    formPageShape,
    PropTypes.arrayOf(formPageShape),
  ]).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleMultiSelect: PropTypes.func.isRequired,
  buttonText: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  formData: PropTypes.shape({}).isRequired,
  errorField: PropTypes.arrayOf(PropTypes.string).isRequired,
  errorMessages: PropTypes.shape({}).isRequired,
  processedLabels: PropTypes.shape({}).isRequired,
  viewMode: PropTypes.bool.isRequired,
  nested: PropTypes.bool.isRequired,
  locked: PropTypes.bool.isRequired,
  level: PropTypes.number.isRequired,
  getUploadUrl: PropTypes.func.isRequired,
  getDownloadUrl: PropTypes.func.isRequired,
};

const createJoiSchema = (formPage, formData) => {
  const schema = {};

  if (!formPage.cards) {
    return schema;
  }

  formPage.cards.forEach((card) => {
    if (!card.fields) {
      return;
    }
    card.fields.forEach((field) => {
      if (field.cond && !field.cond(formData)) {
        return;
      }

      if (field.specialValidation) {
        schema[field.name] = field.specialValidation.label(field.label);
        return;
      }

      switch (field.type) {
        case 'address':
          schema[field.names.country] = Joi.string().required();
          schema[field.names.country] = Joi.string()
            .valid(...countries.map((option) => option.value))
            .label('Country')
            .required();
          schema[field.names.state] =
            formData[field.names.country] === 'US'
              ? Joi.string()
                  .valid(...states.map((option) => option.value))
                  .label('State')
                  .messages({
                    'any.only': '"State/Province" is not allowed to be empty',
                  })
                  .required()
              : Joi.string().label('State/Province').required();
          schema[field.names.city] = Joi.string().required().label('City');
          schema[field.names.addressLine1] = Joi.string()
            .required()
            .label('Address Line 1');
          schema[field.names.addressLine2] = Joi.string()
            .allow('')
            .allow(null)
            .label('Address Line 2');
          schema[field.names.postalCode] = Joi.string()
            .required()
            .label('Postal/ZIP Code');
          break;
        case 'text':
          schema[field.name] = field.required
            ? Joi.string().required().label(field.label)
            : Joi.string().allow('').allow(null).label(field.label);
          break;
        case 'radios':
          if (field.options && field.options.length > 0) {
            schema[field.name] = Joi.string()
              .required()
              .valid(...field.options.map((option) => option.value))
              .label(field.label);
          } else {
            schema[field.name] = field.required
              ? Joi.string().required().label(field.label)
              : Joi.string().allow('').label(field.label);
          }
          break;
        case 'file':
          schema[field.name] = field.required
            ? Joi.bool().required().valid(true).label(field.label)
            : Joi.bool().label(field.label);
          break;
        case 'checkbox':
          if (field.options && field.options.length > 0) {
            schema[field.name] = Joi.array()
              .items(
                Joi.string().valid(
                  ...field.options.map((option) => option.value),
                ),
              )
              .min(field.min || 1)
              .required()
              .label(field.label);
          } else {
            schema[field.name] = Joi.array().label(field.label);
          }
          break;
        case 'yes_no':
          schema[field.name] = Joi.string()
            .required()
            .valid('yes', 'no')
            .label(field.label);
          break;
        case 'select':
          if (field.options && field.options.length > 0) {
            const baseSchema = field.multiple
              ? Joi.array()
                  .items(
                    Joi.string().valid(
                      ...field.options.map((option) => option.value),
                    ),
                  )
                  .min(field.min || 1)
                  .label(field.label)
              : Joi.string()
                  .valid(...field.options.map((option) => option.value))
                  .label(field.label);
            schema[field.name] = field.required
              ? baseSchema.required()
              : baseSchema;
          } else {
            schema[field.name] = Joi.string().allow('').label(field.label);
          }
          break;
        case 'text_or_select': {
          const isSelect = field.condSelect(formData);
          const baseSchema = isSelect
            ? Joi.string()
                .valid(...field.options.map((option) => option.value))
                .label(field.labelSelect)
            : Joi.string().label(field.label);
          schema[field.name] = field.required
            ? baseSchema.required()
            : baseSchema.allow('');
          break;
        }
        case 'subForm':
          schema[field.name] = Joi.array()
            .min(field.min || 0)
            .label(field.label)
            .messages({
              'array.min': `{#label} must contain at least {#limit} item${field.min > 1 ? 's' : ''}`,
            });
          break;
        default:
          schema[field.name] = Joi.any().label(field.label);
          break;
      }
    });
  });

  return Joi.object(schema);
};

function BuildForm({
  formPage,
  onSubmit,
  viewMode = false,
  buttonText = 'Submit',
  formsData,
  nested = false,
  locked = false,
  level = 0,
  onFieldChange = () => {},
  getUploadUrl = () => Promise.reject(new Error('Missing upload url getter')),
  getDownloadUrl = () =>
    Promise.reject(new Error('Missing download url getter')),
}) {
  const [formData, setFormData] = useState(
    initializeFormData(formPage, formsData),
  );
  const [errorField, setErrorField] = useState([]);
  const [errorMessages, setErrorMessages] = useState({});

  useEffect(() => {
    setFormData(initializeFormData(formPage, formsData));
  }, [formsData]);

  const processedLabels = useMemo(() => {
    const processed = {};
    if (formPage.cards) {
      formPage.cards.forEach((card) => {
        card.fields.forEach((field) => {
          processed[field.name] = field.label.replace(
            /\$\$(.*?)\$\$/g,
            (match, p1) => formsData[p1] || match,
          );
        });
      });
    }
    return processed;
  }, [formPage.cards]);

  const runValidation = () => {
    const validationSchema = createJoiSchema(formPage, formData);
    const result = validationSchema.validate(formData, { abortEarly: false });

    if (result.error) {
      const errors = result.error.details.map((detail) => detail.context.key);
      setErrorMessages(
        result.error.details.reduce((acc, item) => {
          const { key } = item.context;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item.message);
          return acc;
        }, {}),
      );
      setErrorField(errors);
      const firstErrorElement = document.querySelector('.error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return false;
    }
    setErrorField([]);
    return true;
  };

  const debouncedFunctionsRef = useRef({});

  const getDebouncedFunction = (field) => {
    if (!debouncedFunctionsRef.current[field]) {
      debouncedFunctionsRef.current[field] = debounce((f, value) => {
        onFieldChange(f, value);
      }, 2000);
    }
    return debouncedFunctionsRef.current[field];
  };

  const handleChange = useCallback((field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    const debouncedFunction = getDebouncedFunction(field);
    debouncedFunction(field, value);
  }, []);

  const handleMultiSelect = useCallback((field, value) => {
    setFormData((prevData) => {
      const isSelected = prevData[field].includes(value);

      const newValues = isSelected
        ? prevData[field].filter((item) => item !== value)
        : [...prevData[field], value];

      const debouncedFunction = getDebouncedFunction(field);
      debouncedFunction(field, newValues);
      return {
        ...prevData,
        [field]: newValues,
      };
    });
  }, []);

  useEffect(() => {
    const updatedFormData = evaluateConditions(formData, formPage);
    if (updatedFormData !== formData) {
      setFormData(updatedFormData);
    }
  }, [formData]);

  useEffect(() => {
    if (errorField.length > 0) {
      const firstErrorElement = document.querySelector('.error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errorField]);

  const handleSubmit = (event) => {
    event.preventDefault();

    const isValid = runValidation();
    if (!isValid) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <div>
      <RenderFormPage
        formPage={formPage}
        handleChange={handleChange}
        handleMultiSelect={handleMultiSelect}
        buttonText={buttonText}
        handleSubmit={handleSubmit}
        formData={formData}
        errorField={errorField}
        errorMessages={errorMessages}
        processedLabels={processedLabels}
        viewMode={viewMode}
        nested={nested}
        locked={locked}
        level={level}
        getUploadUrl={getUploadUrl}
        getDownloadUrl={getDownloadUrl}
      />
    </div>
  );
}

BuildForm.propTypes = {
  formPage: PropTypes.oneOfType([
    formPageShape,
    PropTypes.arrayOf(formPageShape),
  ]).isRequired,
  onSubmit: PropTypes.func.isRequired,
  formsData: PropTypes.shape({}).isRequired,
  viewMode: PropTypes.bool,
  buttonText: PropTypes.string,
  nested: PropTypes.bool,
  locked: PropTypes.bool,
  level: PropTypes.number,
  onFieldChange: PropTypes.func,
  getUploadUrl: PropTypes.func,
  getDownloadUrl: PropTypes.func,
};

export default BuildForm;

export { evaluateConditions, createJoiSchema };
